import { FC, useEffect, useRef, useState } from "react";
import "./Chats.scss";
import ChatInput from "../../components/Chats/ChatInput/ChatInput";
import { useChats } from "../../providers/Chats/ChatsProvider";
import MainNavbar from "../../components/MainNavbar/MainNavbar";
import ChatMessageList from "../../components/Chats/ChatMessageList/ChatMessageList";
import { useAuth } from "../../providers/AuthProvider";
import { useNavigate } from "react-router-dom";
import { ChatsContext } from "../../contexts/ChatsContext";
import { useUsers } from "../../providers/Access/Users/UsersProvider";
import { useChatHighlights } from "../../providers/ChatHighlights/ChatHighlightsProvider";
import ChatHighlightModal from "../../components/Chats/ChatHighlightModal/ChatHighlightModal";
import SimulationBank from "../../components/Chats/SimulationBank/SimulationBank";
import { SimulationBankProvider } from "../../providers/SimulationBank/SimulationBankProvider";
import InssMargin from "../../components/Chats/InssMargin/InssMargin";
import { InssMarginProvider } from "../../providers/InssMargin/InssMarginProvider";
import ChatSidebar from "../../components/Chats/ChatSidebar/ChatSidebar";
import Spinner from "../../components/Spinner/Spinner";
import { UseService } from "../../services/Profile/UseService";
import { BvIcon } from 'bevi-icon';
import { ChatAssistant } from "../../models/Chat/ChatAssistantModel";
import { ChatMessageModel } from "../../models/Chat/ChatMessageModel";
import { ChatsService } from "../../services/Chats/ChatsService";
import { chatFeatures } from "../../config/chatFeatures";
import {
  saveActiveSimulationAssistant,
  loadActiveSimulationAssistant,
  clearActiveSimulationAssistant,
} from "../../utils/SimulationSessionCache";

const SIDEBAR_COLLAPSED_W = 56;
const SIDEBAR_EXPANDED_W = 280;

// Comportamento frontend por ícone — payload inicial e qual componente renderizar
const ASSISTANT_BEHAVIOR: Record<string, { payload?: string; isSimulation?: boolean; startInQuery?: boolean; isInssMargin?: boolean }> = {
  description: { isSimulation: true },
  queryStats:  { isSimulation: true, startInQuery: true },
  lockPerson:  { payload: 'Consultar status de protocolo de login do banco' },
  // Pré-digitação do Aumento de Margem INSS — renderiza a carteira no lugar do chat.
  // Fora do bloqueio SIMULATION_TOOLS_BLOCKED_USER_TYPE_IDS: qualquer logado acessa.
  moneyUp:     { isInssMargin: true },
};

// Bloqueio temporário (AI-1136): usuários destes user_type não podem acessar as
// ferramentas de simulação (Simulação e Digitação de Empréstimo Consignado CLT E
// Consulta de Propostas — todo assistente isSimulation, ver ASSISTANT_BEHAVIOR).
// 4 = SUBSTABELECIDO, 40 = INDICADO.
const SIMULATION_TOOLS_BLOCKED_USER_TYPE_IDS = [4, 40];

// content: '' + isStreaming + fullContent reaproveita o mesmo efeito de "digitação"
// usado em toda resposta da IA (ver AgentStreamingMessage, acionado por ChatMessage
// quando isStreaming && content === "" && fullContent).
const buildSimulationBlockedMessage = (toolName?: string): ChatMessageModel => ({
  id: -1,
  sender: 'agent',
  created_at: new Date().toISOString(),
  content: '',
  isStreaming: true,
  fullContent:
    '**Aviso de Restrição de Acesso**\n\n' +
    `A ferramenta de ${toolName || 'simulação'} está temporariamente indisponível para o seu tipo de usuário.\n\n` +
    'Para ter acesso a esta funcionalidade, por favor, faça login com um usuário do tipo **AGENTE**.',
});

interface ChatsProps {}

const Chats: FC<ChatsProps> = () => {
  const messagesContainerRef = useRef<HTMLDivElement | null>(null);
  const messagesWrapperRef = useRef<HTMLDivElement | null>(null);

  const { chat, isStreaming, isCancelling, agentFinalText, isVoting, handleMe, showSimulation, setShowSimulation, handleSend, activeAssistantId, handleSelectAssistant, isLoadingMessages } = useChats();
  const { handleStatusTutorial } = useUsers();
  const { userLogged } = useAuth();
  const { handleGetActive } = useChatHighlights();
  const { handleCompletedToday } = useUsers();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [assistants, setAssistants] = useState<ChatAssistant[]>([]);
  const [messageCount, setMessageCount] = useState<number | undefined>(undefined);
  // Label da etapa atual da simulação (ex.: "Etapa 1/2 - Consulta de Margem"), reportado
  // pelo SimulationBank via callback — sobrescreve activeFeature.name enquanto a simulação
  // estiver aberta (ver onStepLabelChange em SimulationBank.tsx)
  const [simulationStepLabel, setSimulationStepLabel] = useState<string | null>(null);
  // Título do header sobrescrito pelo SimulationBank em telas específicas (ex.: formulário
  // de digitação vira "Digitação da Proposta" em vez do nome padrão do agente).
  const [simulationTitleLabel, setSimulationTitleLabel] = useState<string | null>(null);
  // Evita mostrar o assistant padrão (ex.: Consulta Livre) piscando antes do
  // assistant cacheado/preferido ser selecionado (ex.: logo após um F5)
  const [isInitializingAssistant, setIsInitializingAssistant] = useState(true);

  const navigate = useNavigate();

  const hasSidebar = assistants.length > 0;
  const activeFeature = assistants.find(a => a.id === activeAssistantId);
  const activeBehavior = activeFeature ? (ASSISTANT_BEHAVIOR[activeFeature.icon] ?? {}) : {};

  const sidebarWidth = hasSidebar
    ? sidebarCollapsed
      ? SIDEBAR_COLLAPSED_W
      : SIDEBAR_EXPANDED_W
    : 0;

  const handleFeatureSelect = (id: number | null) => {
    const firstAssistant = assistants[0];
    const newId = id ?? firstAssistant?.id ?? null;
    const assistant = assistants.find(a => a.id === newId);
    const behavior = assistant ? (ASSISTANT_BEHAVIOR[assistant.icon] ?? {}) : {};

    // Mesmo assistant: re-envia payload se não houver mensagens dele
    if (newId === activeAssistantId) {
      const ownMessages = chat?.messages?.filter((m) => m.assistant_id === newId);
      if (behavior.payload && !ownMessages?.length) {
        handleSend(behavior.payload, newId);
      }
      return;
    }

    handleSelectAssistant(newId).then((loadedChat) => {
      if (behavior.isSimulation || behavior.isInssMargin) return;

      const ownMessages = loadedChat?.messages?.filter((m) => m.assistant_id === newId);
      if (!ownMessages?.length && behavior.payload) {
        setTimeout(() => handleSend(behavior.payload!, newId), 800);
      }
    });
  };

  const isSimulationActive = activeBehavior.isSimulation === true;
  const isInssMarginActive = activeBehavior.isInssMargin === true;
  const isSimulationToolBlocked =
    isSimulationActive &&
    SIMULATION_TOOLS_BLOCKED_USER_TYPE_IDS.includes(Number(userLogged?.user_type?.id));

  // Guarda só o id do assistente (nenhum dado de cliente) para um F5 voltar à mesma tela.
  useEffect(() => {
    if (assistants.length === 0) return;
    if ((isSimulationActive || isInssMarginActive) && activeAssistantId != null) {
      saveActiveSimulationAssistant(activeAssistantId);
    } else {
      clearActiveSimulationAssistant();
    }
  }, [assistants.length, isSimulationActive, isInssMarginActive, activeAssistantId]);

  useEffect(() => {
    document.documentElement.style.setProperty("--chat-sidebar-width", `${sidebarWidth}px`);
    document.documentElement.style.setProperty("--chat-sidebar-gap", hasSidebar ? "12px" : "0px");
    return () => {
      document.documentElement.style.removeProperty("--chat-sidebar-width");
      document.documentElement.style.removeProperty("--chat-sidebar-gap");
    };
  }, [sidebarWidth, hasSidebar]);

  // --app-vh: 100dvh mede maior que a tela real em alguns navegadores mobile
  // (confirmado ao vivo: 828px vs innerHeight=720px, sem teclado nem barra do
  // navegador em transição). window.innerHeight bate com a altura visível real e,
  // ao contrário de visualViewport.height, não encolhe quando o teclado abre — por
  // isso é essa a fonte usada aqui (Chats.scss sobrescreve o dvh quebrado com isso).
  useEffect(() => {
    const updateAppHeight = () => {
      document.documentElement.style.setProperty("--app-vh", `${window.innerHeight}px`);
    };

    updateAppHeight();
    window.addEventListener("resize", updateAppHeight);
    window.addEventListener("orientationchange", updateAppHeight);

    return () => {
      window.removeEventListener("resize", updateAppHeight);
      window.removeEventListener("orientationchange", updateAppHeight);
      document.documentElement.style.removeProperty("--app-vh");
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      const result = await handleStatusTutorial();

      // if (!result.success || result.passedTutorial !== 1) {
      //   navigate("/chats/tutorial");
      //   return;
      // }

      handleMe();

      const completedToday = await handleCompletedToday();
      handleGetActive(!completedToday);

      const [balanceResponse] = await new UseService().getMessageBalance();
      if (balanceResponse?.data?.messages_used !== undefined) {
        setMessageCount(balanceResponse.data.messages_used);
      }

      const [assistantsResponse] = await new ChatsService().getAssistants();
      const apiList: ChatAssistant[] | undefined = assistantsResponse?.data;

      if (apiList?.length) {
        setAssistants(apiList);
        const cachedAssistantId = loadActiveSimulationAssistant();
        const preferredId = apiList.some(a => a.id === cachedAssistantId)
          ? cachedAssistantId
          : apiList[0]?.id;
        if (preferredId != null) {
          await handleSelectAssistant(preferredId);
        }
      } else {
        // Fallback para config estática enquanto a API não está disponível
        const fallback: ChatAssistant[] = chatFeatures
          .filter(f => userLogged && f.permissionCheck(userLogged))
          .map((f, i) => ({ id: i + 1, name: f.label, description: '', icon: f.icon, order: i + 1 }));
        setAssistants(fallback);
        const cachedAssistantId = loadActiveSimulationAssistant();
        const preferredFallbackId = fallback.some(a => a.id === cachedAssistantId)
          ? cachedAssistantId
          : fallback[0]?.id;
        if (preferredFallbackId != null) await handleSelectAssistant(preferredFallbackId);
      }

      setIsInitializingAssistant(false);
    };

    load();
  }, []);

  useEffect(() => {
    if (isVoting || !messagesContainerRef.current) return;
    messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
  }, [chat, agentFinalText, isStreaming, isVoting]);

  // Reforça o scroll pro fim quando o CONTEÚDO cresce (ex.: "Esta resposta foi
  // útil?", anexado num commit separado de quando isStreaming vira false — o efeito
  // acima pode rolar cedo demais e deixar esse bloco atrás do ChatInput fixo).
  useEffect(() => {
    const container = messagesContainerRef.current;
    const content = messagesWrapperRef.current;
    if (!container || !content) return;

    const observer = new ResizeObserver(() => {
      if (isVoting) return;
      container.scrollTop = container.scrollHeight;
    });
    observer.observe(content);

    return () => observer.disconnect();
  }, [isVoting]);

  return (
    <>
      <MainNavbar />

      <ChatsContext.Provider>
        <div
          className="chat-page"
          style={{
            paddingTop: "var(--navbar-height)",
            "--background-image": `url(${process.env.PUBLIC_URL}/assets/img/background-wrapper.png)`,
          } as React.CSSProperties}
        >
          <div className="chat-layout-body">
            {hasSidebar && (
              <ChatSidebar
                assistants={assistants}
                activeAssistantId={activeAssistantId}
                onAssistantSelect={handleFeatureSelect}
                onCollapsedChange={setSidebarCollapsed}
                messageCount={messageCount}
              />
            )}

            <div
              id="chats-card"
              className="Chats flex flex-column"
              data-testid="Chats"
              data-sidebar-collapsed={sidebarCollapsed ? "true" : "false"}
            >
              {activeFeature && (
                <div className="chat-agent-header">
                  <div className="chat-agent-header__row">
                    {activeBehavior.isSimulation && !isSimulationToolBlocked ? (
                      <>
                        {simulationTitleLabel && <BvIcon name={activeFeature.icon} size={1.4} variant="solid" />}
                        {simulationTitleLabel && <span className="chat-agent-header__label">{simulationTitleLabel}</span>}
                      </>
                    ) : (
                      <>
                        <BvIcon name={activeFeature.icon} size={1.4} variant="solid" />
                        <span className="chat-agent-header__label">{simulationTitleLabel || activeFeature.name}</span>
                      </>
                    )}
                  </div>
                  {simulationStepLabel && !isSimulationToolBlocked && (
                    <span className="chat-agent-header__subtitle">{simulationStepLabel}</span>
                  )}
                  {isInssMarginActive && (
                    <span className="chat-agent-header__subtitle">
                      Aqui você acessa sua carteira de clientes dos últimos 3 anos.
                      <br />
                      Em breve, também poderá qualificar dados, simular e digitar propostas em várias financeiras, tudo em um só lugar.
                    </span>
                  )}
                </div>
              )}
              {isInitializingAssistant ? (
                <div className="chat-messages-container flex-bgs">
                  <Spinner position="position-absolute" backgroundColor="transparent" opacity="1" />
                </div>
              ) : isSimulationToolBlocked ? (
                // AI-1136: usuário SUBSTABELECIDO/INDICADO — a ferramenta nunca chega a
                // montar (sem SimulationBank, sem ChatInput), apenas o aviso de bloqueio.
                <div className="chat-messages-container flex-bgs">
                  <div className="messages-wrapper">
                    <ChatMessageList messages={[buildSimulationBlockedMessage(activeFeature?.name)]} />
                  </div>
                </div>
              ) : activeBehavior.isSimulation ? (
                <div className="simulation-panel">
                  {/* key força remount ao trocar de assistente (ex.: CLT -> Consulta de Propostas),
                      já que ambos usam este mesmo componente e cada um começa em uma fase distinta. */}
                  <SimulationBankProvider key={activeAssistantId}>
                    <SimulationBank
                      key={activeAssistantId}
                      onClose={() => handleFeatureSelect(null)}
                      onStepLabelChange={setSimulationStepLabel}
                      onTitleChange={setSimulationTitleLabel}
                      startInQuery={activeBehavior.startInQuery}
                    />
                  </SimulationBankProvider>
                </div>
              ) : activeBehavior.isInssMargin ? (
                <div className="inss-margin-panel">
                  <InssMarginProvider key={activeAssistantId}>
                    <InssMargin />
                  </InssMarginProvider>
                </div>
              ) : (
                <>
                  <div className="chat-messages-container flex-bgs" ref={messagesContainerRef}>
                    {isLoadingMessages && <Spinner position="position-absolute" backgroundColor="transparent" opacity="1" />}
                    <div className="messages-wrapper" ref={messagesWrapperRef}>
                      <ChatMessageList
                        messages={chat?.messages?.filter(
                          (m) =>
                            m.assistant_id == null ||
                            activeAssistantId == null ||
                            Number(m.assistant_id) === Number(activeAssistantId)
                        )}
                        isStreaming={isStreaming}
                        isCancelling={isCancelling}
                      />
                      {showSimulation && (
                        <SimulationBankProvider>
                          <SimulationBank
                            onClose={() => setShowSimulation(false)}
                            onStepLabelChange={setSimulationStepLabel}
                            onTitleChange={setSimulationTitleLabel}
                          />
                        </SimulationBankProvider>
                      )}
                    </div>
                  </div>

                  <ChatInput />
                </>
              )}
            </div>
          </div>
        </div>

        <ChatHighlightModal />
      </ChatsContext.Provider>
    </>
  );
};

export default Chats;
